import { useEffect, useMemo, useRef, useState } from "react";

import { Button } from "@/components/ui";
import type { TestCaseListItem } from "@/types";

type GroupBy = "category" | "date";

const GROUP_LABEL: Record<GroupBy, string> = {
  category: "피드백 항목별",
  date: "날짜별 (승격일)",
};

type Group = { key: string; label: string; members: TestCaseListItem[] };

function groupCases(cases: readonly TestCaseListItem[], by: GroupBy): Group[] {
  const map = new Map<string, Group>();
  for (const tc of cases) {
    // One group per TC. A TC with two categories under both would be counted
    // twice by the group checkboxes; the first chip is the promoting 👎's.
    const key =
      by === "category"
        ? tc.categories[0] ?? "(분류 없음)"
        : (tc.promoted_at ?? "").slice(0, 10) || "(날짜 없음)";
    const group = map.get(key) ?? { key, label: key, members: [] };
    group.members.push(tc);
    map.set(key, group);
  }
  const groups = [...map.values()];
  return by === "date"
    ? groups.sort((a, b) => b.key.localeCompare(a.key))
    : groups.sort((a, b) => a.key.localeCompare(b.key));
}

/**
 * The one place in the tab to pick several TCs (fe/TEST_CASES_BATCH.md §2).
 *
 * Selection is by id, so regrouping keeps it. "전체" means **what is listed** —
 * the label says so, because a checkbox that quietly means "everything
 * matching" fixes a set the reader never saw. A selection that is not in the
 * list (e.g. a TC retired after it was handed over) is said out loud rather
 * than dropped. And it has no action button: the host decides what the
 * selection is for.
 *
 * `cases` is what may be added. `held` is what is already selected but may
 * not be — listed apart, with the reason, so it can be seen and taken out but
 * never picked again. Seeing a row and being allowed to add it are separate.
 */
export type HeldCase = { tc: TestCaseListItem; reason: string };

export function TcPicker({
  cases,
  held = [],
  selected,
  single,
  onChange,
}: {
  cases: readonly TestCaseListItem[];
  held?: readonly HeldCase[];
  selected: readonly string[];
  single: boolean;
  onChange: (next: string[]) => void;
}) {
  const [groupBy, setGroupBy] = useState<GroupBy>("category");
  const groups = useMemo(() => groupCases(cases, groupBy), [cases, groupBy]);
  const listed = useMemo(() => new Set(cases.map((c) => c.test_case_id)), [cases]);
  const picked = new Set(selected);
  const heldIds = new Set(held.map((h) => h.tc.test_case_id));
  const offList = selected.filter((id) => !listed.has(id) && !heldIds.has(id)).length;
  const allOn = cases.length > 0 && cases.every((c) => picked.has(c.test_case_id));

  const toggle = (id: string) => {
    if (single) return onChange([id]);
    onChange(picked.has(id) ? selected.filter((x) => x !== id) : [...selected, id]);
  };
  const setGroup = (group: Group, on: boolean) => {
    const ids = group.members.map((m) => m.test_case_id);
    onChange(on ? [...new Set([...selected, ...ids])] : selected.filter((x) => !ids.includes(x)));
  };

  return (
    <div className="grid gap-2 rounded-lg border border-border">
      <div className="flex flex-wrap items-center gap-2 border-b border-border px-3 py-2">
        <span className="text-xs font-medium">
          TC 선택 · {single ? "정확히 1 개" : "2 개 이상"}
        </span>
        <select
          aria-label="묶는 기준"
          value={groupBy}
          onChange={(e) => setGroupBy(e.target.value as GroupBy)}
          className="rounded-md border border-border bg-surface-input px-2 py-1 text-xs"
        >
          {(Object.keys(GROUP_LABEL) as GroupBy[]).map((key) => (
            <option key={key} value={key}>
              {GROUP_LABEL[key]}
            </option>
          ))}
        </select>
        <span className="text-xs text-muted-foreground tabular-nums">
          {selected.length} 선택
          {heldIds.size > 0 && ` (추가할 수 없는 ${heldIds.size} 건 포함)`}
          {offList > 0 && ` (목록에 없는 ${offList} 건 포함)`}
        </span>
        {!single && (
          <Button
            variant="outline"
            size="sm"
            className="ml-auto"
            onClick={() =>
              onChange(
                allOn
                  ? selected.filter((id) => !listed.has(id))
                  : [...new Set([...selected, ...cases.map((c) => c.test_case_id)])],
              )
            }
          >
            {allOn ? "보이는 것 전체 해제" : `보이는 것 전체 (${cases.length})`}
          </Button>
        )}
      </div>

      {held.length > 0 && (
        <div className="grid gap-1 border-b border-border px-1 pb-2">
          <div className="px-2 pt-1 text-xs font-medium text-muted-foreground">
            이미 선택됐지만 추가할 수 없음 · 해제만 가능
          </div>
          <ul aria-label="추가할 수 없는 선택">
            {held.map(({ tc, reason }) => (
              <li key={tc.test_case_id}>
                <label className="flex cursor-pointer items-center gap-2 rounded-md py-1 pr-2 pl-6 text-[13px] text-muted-foreground hover:bg-accent-hover">
                  <input
                    type="checkbox"
                    checked
                    onChange={() => onChange(selected.filter((x) => x !== tc.test_case_id))}
                    aria-label={`${tc.trigger_preview || tc.title || tc.test_case_id.slice(0, 8)} 선택 해제`}
                    className="accent-primary"
                  />
                  <span className="min-w-0 flex-1 truncate" title={tc.trigger_preview ?? undefined}>
                    {tc.trigger_preview || tc.title || "—"}
                  </span>
                  <span className="shrink-0 text-[11px]">{reason}</span>
                </label>
              </li>
            ))}
          </ul>
        </div>
      )}

      <ul className="max-h-80 overflow-y-auto px-1 pb-2">
        {groups.map((group) => {
          const count = group.members.filter((m) => picked.has(m.test_case_id)).length;
          return (
            <li key={group.key}>
              <GroupRow
                label={group.label}
                count={count}
                total={group.members.length}
                single={single}
                onToggle={(on) => setGroup(group, on)}
              />
              <ul>
                {group.members.map((tc) => (
                  <li key={tc.test_case_id}>
                    <label className="flex cursor-pointer items-center gap-2 rounded-md py-1 pr-2 pl-6 text-[13px] hover:bg-accent-hover">
                      <input
                        type={single ? "radio" : "checkbox"}
                        name={single ? "tc-pick" : undefined}
                        checked={picked.has(tc.test_case_id)}
                        onChange={() => toggle(tc.test_case_id)}
                        className="accent-primary"
                      />
                      <span className="min-w-0 flex-1 truncate" title={tc.trigger_preview ?? undefined}>
                        {tc.trigger_preview || tc.title || "—"}
                      </span>
                      <span className="shrink-0 text-[11px] text-muted-foreground" title={tc.test_case_id}>
                        {tc.test_case_id.slice(0, 8)} · {tc.source_environment}
                      </span>
                    </label>
                  </li>
                ))}
              </ul>
            </li>
          );
        })}
      </ul>
    </div>
  );
}

function GroupRow({
  label,
  count,
  total,
  single,
  onToggle,
}: {
  label: string;
  count: number;
  total: number;
  single: boolean;
  onToggle: (on: boolean) => void;
}) {
  const ref = useRef<HTMLInputElement>(null);
  useEffect(() => {
    if (ref.current) ref.current.indeterminate = count > 0 && count < total;
  }, [count, total]);
  return (
    <label className="mt-1 flex items-center gap-2 rounded-md px-2 py-1 text-xs font-medium text-muted-foreground">
      {!single && (
        <input
          ref={ref}
          type="checkbox"
          checked={count === total}
          onChange={(e) => onToggle(e.target.checked)}
          aria-label={`${label} 전체`}
          className="accent-primary"
        />
      )}
      <span className="flex-1">{label}</span>
      <span className="tabular-nums">
        {count} / {total}
      </span>
    </label>
  );
}
